// ProfileBuzzPosts — Kullanicinin RunBuzz'da paylastigi postlar
// API: GET /api/buzz/user/{username}/posts
import { ExternalLink, Heart, MessageCircle, Share2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { API_URL, apiFetch } from '../../config/api'

export interface BuzzPost {
  id: string
  content: string
  media_url?: string
  media_type?: 'image' | 'video'
  likes_count: number
  comments_count: number
  shares_count: number
  created_at: string
  show_on_profile: boolean
  ai_creation_id?: string
  ai_tool?: string
}

interface Props {
  username: string
  loading?: boolean
}

export default function ProfileBuzzPosts({ username }: Props) {
  const [posts, setPosts] = useState<BuzzPost[]>([])
  const [loading, setLoading] = useState(true)

  const loadPosts = useCallback(() => {
    setLoading(true)
    apiFetch(`${API_URL}/api/buzz/user/${encodeURIComponent(username)}/posts`)
      .then(r => r.ok ? r.json() : { posts: [] })
      .then((d: any) => {
        const items: BuzzPost[] = (d.posts ?? d.items ?? []).map((p: any) => ({
          id: p.id,
          content: p.content ?? p.text ?? '',
          media_url: p.media_url ?? p.image_url ?? p.video_url,
          media_type: p.media_type ?? (p.video_url ? 'video' : p.image_url ? 'image' : undefined),
          likes_count: p.likes_count ?? p.likes ?? 0,
          comments_count: p.comments_count ?? p.comments ?? 0,
          shares_count: p.shares_count ?? p.shares ?? 0,
          created_at: p.created_at,
          show_on_profile: p.show_on_profile ?? true,
          ai_creation_id: p.ai_creation_id,
          ai_tool: p.ai_tool,
        }))
        setPosts(items.filter(p => p.show_on_profile))
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [username])

  useEffect(() => { loadPosts() }, [loadPosts])

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} style={{
            height: 120, borderRadius: 14, background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border-default, #30363d)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
        ))}
      </div>
    )
  }

  if (posts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-secondary, #8b949e)' }}>
        <MessageCircle size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
        <p style={{ fontSize: 14 }}>No Buzz posts shared to profile</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {posts.map(post => (
        <article
          key={post.id}
          style={{
            padding: 18, borderRadius: 14,
            border: '1px solid var(--border-default, #30363d)',
            background: 'rgba(255,255,255,0.02)',
            backdropFilter: 'blur(8px)',
            transition: 'border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea44' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-default, #30363d)' }}
        >
          {/* Icerik */}
          <p style={{
            fontSize: 14, color: 'var(--text-primary, #e6edf3)',
            lineHeight: 1.7, margin: '0 0 12px',
            display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {post.content}
          </p>

          {/* Media */}
          {post.media_url && (
            <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 12, maxHeight: 280 }}>
              {post.media_type === 'video' ? (
                <video src={post.media_url} controls preload="metadata" style={{ width: '100%', maxHeight: 280, objectFit: 'cover' }} />
              ) : (
                <img src={post.media_url} alt="" loading="lazy" style={{ width: '100%', maxHeight: 280, objectFit: 'cover' }} />
              )}
            </div>
          )}

          {/* AI tool badge */}
          {post.ai_tool && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 6, marginBottom: 10,
              fontSize: 11, fontWeight: 600,
              background: 'rgba(102,126,234,0.1)', color: '#667eea',
            }}>
              Created with {post.ai_tool}
            </span>
          )}

          {/* Footer — stats + link */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
              <Heart size={13} /> {post.likes_count}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
              <MessageCircle size={13} /> {post.comments_count}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
              <Share2 size={13} /> {post.shares_count}
            </span>
            <a
              href={`/buzz/post/${post.id}`}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 12, color: '#667eea', textDecoration: 'none', fontWeight: 600,
              }}
            >
              <ExternalLink size={11} /> View on Buzz
            </a>
          </div>

          {/* Tarih */}
          <p style={{ fontSize: 11, color: 'var(--text-secondary, #8b949e)', margin: '8px 0 0' }}>
            {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </article>
      ))}
    </div>
  )
}
